/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { Avatar } from "../WorkDay/shared/Avatar";
import { localDate } from "../WorkDay/shared/dates";
import { shiftHours, weekKey } from "../WorkDay/Rota/engine/rules";
import type { LinkView } from "./types";

type Props = { token: string; view: LinkView; onChanged: () => void };

const dayLabel = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

// Upcoming shifts, week by week, with who else is working.
export const MyShifts = defineComponent<Props>({
  name: "MyShifts",
  props: ["token", "view", "onChanged"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const today = localDate();
    const busy = ref<number | null>(null);

    const offerOf = (shiftId: number) => props.view.requests.find((r) => r.shift_id === shiftId && r.kind === "offer" && r.status === "pending");

    // Give a shift away: once a manager approves, it becomes open for others.
    const offer = async (shiftId: number) => {
      if (!confirm(t("teamlink.page.offer_confirm"))) return;
      busy.value = shiftId;
      try {
        await trpc.teamLink.requestShift.mutate({ token: props.token, shift_id: shiftId, kind: "offer", today });
        toast.showToast(t("teamlink.page.offer_sent"), { type: "success" });
        props.onChanged();
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("teamlink.page.failed"), { type: "error" });
      } finally {
        busy.value = null;
      }
    };

    const withdraw = async (id: number) => {
      await trpc.teamLink.cancelShiftRequest.mutate({ token: props.token, id });
      props.onChanged();
    };

    const weeks = computed(() => {
      const upcoming = props.view.shifts.filter((s) => s.shift_date >= today);
      const groups = new Map<number, LinkView["shifts"]>();
      for (const s of upcoming) {
        const k = weekKey(s.shift_date);
        groups.set(k, [...(groups.get(k) ?? []), s]);
      }
      return [...groups.entries()].map(([key, shifts]) => ({
        key,
        shifts,
        hours: shifts.reduce((sum, s) => sum + shiftHours({ ...s, id: s.id, role_id: null, staff_id: null }), 0),
      }));
    });

    return () => {
      if (!weeks.value.length) {
        return (
          <div class="rounded-2xl bg-base-100 p-8 text-center">
            <span class="material-symbols-outlined text-5xl text-base-content/30">event_available</span>
            <p class="mt-2 text-sm text-base-content/60">{t("teamlink.page.no_shifts")}</p>
          </div>
        );
      }
      return (
        <div class="space-y-6">
          {weeks.value.map((w, i) => (
            <section key={w.key} class="space-y-2">
              <div class="flex items-baseline justify-between px-1">
                <h2 class="text-sm font-semibold uppercase tracking-wide text-base-content/60">
                  {i === 0 && w.shifts[0].shift_date <= today ? t("teamlink.page.this_week") : t("teamlink.page.week_of").replace("{date}", new Date(`${w.shifts[0].shift_date}T12:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" }))}
                </h2>
                <span class="text-xs text-base-content/50">{t("teamlink.page.hours").replace("{hours}", w.hours.toFixed(1))}</span>
              </div>
              {w.shifts.map((s) => {
                const isToday = s.shift_date === today;
                return (
                  <div key={s.id} class={["rounded-2xl border bg-base-100 p-4", isToday ? "border-primary ring-2 ring-primary/20" : "border-base-300"].join(" ")}>
                    <div class="flex items-start justify-between gap-3">
                      <div>
                        <div class="text-xs font-medium uppercase text-base-content/50">{isToday ? t("teamlink.page.today") : dayLabel(s.shift_date)}</div>
                        <div class="text-2xl font-semibold tabular-nums">{`${s.start_time} – ${s.end_time}`}</div>
                        <div class="text-sm text-base-content/70">{[s.role_name, s.block_name].filter(Boolean).join(" · ")}</div>
                      </div>
                      {s.break_minutes > 0 && <span class="badge badge-ghost badge-sm">{t("teamlink.page.break").replace("{min}", String(s.break_minutes))}</span>}
                    </div>
                    <div class="mt-3 flex justify-end">
                      {offerOf(s.id) ? (
                        <span class="flex items-center gap-2">
                          <span class="badge badge-warning badge-outline badge-sm">{t("teamlink.page.offer_pending")}</span>
                          <button class="btn btn-ghost btn-xs" onClick={() => withdraw(offerOf(s.id)!.id)}>
                            {t("teamlink.page.cancel")}
                          </button>
                        </span>
                      ) : (
                        <button class="btn btn-ghost btn-xs" disabled={busy.value === s.id} onClick={() => offer(s.id)}>
                          <span class="material-symbols-outlined text-sm">swap_horiz</span>
                          {t("teamlink.page.offer")}
                        </button>
                      )}
                    </div>
                    {s.colleagues.length > 0 && (
                      <div class="mt-3 flex items-center gap-2">
                        <div class="flex -space-x-2">
                          {s.colleagues.slice(0, 5).map((name) => (
                            <span key={name} class="rounded-full ring-2 ring-base-100">
                              <Avatar name={name} size="xs" />
                            </span>
                          ))}
                        </div>
                        <span class="truncate text-xs text-base-content/60">{t("teamlink.page.with").replace("{names}", s.colleagues.join(", "))}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          ))}
        </div>
      );
    };
  },
});
